import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";

export default function CampaignModal({ isOpen, onClose, campaign, onSave }) {
  const [name, setName] = useState(campaign?.name || "");
  const [description, setDescription] = useState(campaign?.description || "");
  const [isActive, setIsActive] = useState(campaign?.status === "active");
  const [formErrors, setFormErrors] = useState({ name: "", description: "" });

  // Reset form and errors when modal opens or campaign changes
  useEffect(() => {
    if (isOpen) {
      setName(campaign?.name || "");
      setDescription(campaign?.description || "");
      setIsActive(campaign?.status === "active");
      setFormErrors({ name: "", description: "" });
    }
  }, [isOpen, campaign]);

  if (!isOpen) return null;

  const validateForm = () => {
    const errors = { name: "", description: "" };
    let isValid = true;

    if (!name || name.trim() === "") {
      errors.name = "Campaign name is required";
      isValid = false;
    }

    // if (!description || description.trim() === "") {
    //   errors.description = "Description is required";
    //   isValid = false;
    // }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form inputs
    if (!validateForm()) {
      return;
    }

    // Pass validated data to parent
    onSave({
      name,
      description,
      status: isActive ? "active" : "inactive",
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-2xl shadow-xl w-full max-w-lg border border-border">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">
            {campaign ? "Edit Campaign" : "Create Campaign"}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-secondary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name">Campaign Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter campaign name"
              required
              className={formErrors.name ? "border-red-500" : ""}
            />
            {formErrors.name && (
              <p className="text-sm text-red-500">{formErrors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter campaign description"
              rows={4}
              className={formErrors.description ? "border-red-500" : ""}
            />
            {formErrors.description && (
              <p className="text-sm text-red-500">{formErrors.description}</p>
            )}
          </div>

          {campaign?.name != "all" && (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <p className="text-sm text-muted-foreground">
                  {isActive ? "Campaign is active" : "Campaign is inactive"}
                </p>
              </div>
              <Switch
                id="status"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              {campaign ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
